<script setup>
import { Badge } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import LongText from '../../../components/LongText.vue'

const props = defineProps({
  message: { type: Object, required: true },
  showNumber: { type: Boolean, default: true },
  collapsible: { type: Boolean, default: false },
})

const { t } = useI18n()

function messageLabel(role) {
  switch (role) {
    case 'system':
    case 'user':
    case 'assistant':
      return t(`visualGameAdmin.llm.conversations.detail.${role}`)
    default:
      throw new Error(`Unhandled conversation message role: ${role}`)
  }
}

function badgeVariant(role) {
  switch (role) {
    case 'system':
      return 'neutral'
    case 'user':
      return 'info'
    case 'assistant':
      return 'success'
    default:
      throw new Error(`Unhandled conversation message role: ${role}`)
  }
}

function jsonEntries() {
  if (props.message.content_type !== 'json') {
    throw new Error(`jsonEntries requires a JSON message, index: ${props.message.index}`)
  }
  return Object.entries(props.message.content)
}

function fieldText(value) {
  if (value !== null && typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

function rawJson() {
  if (props.message.content_type !== 'json') {
    throw new Error(`rawJson requires a JSON message, index: ${props.message.index}`)
  }
  return JSON.stringify(props.message.content)
}

function rawText() {
  switch (props.message.content_type) {
    case 'text':
      return props.message.content
    case 'json':
      return JSON.stringify(props.message.content, null, 2)
    default:
      throw new Error(`Unhandled conversation content type: ${props.message.content_type}`)
  }
}

function markdownText() {
  const raw = rawText()
  return props.message.content_type === 'json' ? `\`\`\`json\n${raw}\n\`\`\`` : raw
}

function longTextLabel() {
  const role = messageLabel(props.message.role)
  if (!props.showNumber) return role
  return `${role} · ${t('visualGameAdmin.llm.conversations.detail.messageNumber', { number: props.message.index + 1 })}`
}
</script>

<template>
  <li class="conversation-message" :class="`conversation-message--${message.role}`">
    <div v-if="collapsible" class="conversation-message__prompt">
      <LongText
        :label="longTextLabel()"
        :text="markdownText()"
        :copy-text="rawText()"
        :collapse-over="0"
        markdown
      />
    </div>

    <details v-else-if="message.content_type === 'json'" class="conversation-message__bubble conversation-message__json">
      <summary class="conversation-message__head">
        <Badge :variant="badgeVariant(message.role)" size="sm">{{ messageLabel(message.role) }}</Badge>
        <span v-if="showNumber">
          {{ t('visualGameAdmin.llm.conversations.detail.messageNumber', { number: message.index + 1 }) }}
        </span>
      </summary>
      <dl class="conversation-message__fields">
        <div v-for="entry in jsonEntries()" :key="entry[0]">
          <dt>{{ entry[0] }}</dt>
          <dd><pre>{{ fieldText(entry[1]) }}</pre></dd>
        </div>
      </dl>

      <details class="conversation-message__raw">
        <summary>{{ t('visualGameAdmin.llm.conversations.detail.rawJson') }}</summary>
        <pre>{{ rawJson() }}</pre>
      </details>
    </details>

    <div v-else class="conversation-message__bubble">
      <header class="conversation-message__head">
        <Badge :variant="badgeVariant(message.role)" size="sm">{{ messageLabel(message.role) }}</Badge>
        <span v-if="showNumber">
          {{ t('visualGameAdmin.llm.conversations.detail.messageNumber', { number: message.index + 1 }) }}
        </span>
      </header>
      <p class="conversation-message__text">{{ message.content }}</p>
    </div>
  </li>
</template>

<style scoped>
.conversation-message {
  display: flex;
}

.conversation-message--user {
  justify-content: flex-end;
}

.conversation-message--system {
  justify-content: center;
}

.conversation-message__bubble {
  width: min(72%, 680px);
  min-width: 0;
  padding: var(--drama-space-lg);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-lg);
  background: var(--drama-color-surface-alt);
}

.conversation-message__prompt {
  width: 100%;
  min-width: 0;
}

.conversation-message--user .conversation-message__bubble {
  border-color: color-mix(in srgb, var(--drama-color-info) 45%, var(--drama-color-border));
  background: color-mix(in srgb, var(--drama-color-info) 9%, var(--drama-color-surface));
}

.conversation-message--assistant .conversation-message__bubble {
  border-color: color-mix(in srgb, var(--drama-color-success) 38%, var(--drama-color-border));
}

.conversation-message--system .conversation-message__bubble {
  width: 100%;
}

.conversation-message__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-md);
  margin-bottom: var(--drama-space-md);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.conversation-message__json > .conversation-message__head {
  cursor: pointer;
  margin-bottom: 0;
  padding: var(--drama-space-sm) var(--drama-space-md);
}

.conversation-message__json[open] > .conversation-message__head {
  margin-bottom: var(--drama-space-md);
}

.conversation-message__json {
  padding: 0;
}

.conversation-message__json > .conversation-message__fields {
  padding: 0 var(--drama-space-md) var(--drama-space-md);
}

.conversation-message__json > .conversation-message__raw {
  margin: 0 var(--drama-space-md) var(--drama-space-md);
}

.conversation-message__text {
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  line-height: var(--drama-line-height-relaxed);
}

.conversation-message__fields {
  display: grid;
  gap: var(--drama-space-md);
  margin: 0;
}

.conversation-message__fields > div {
  display: grid;
  grid-template-columns: minmax(120px, 28%) minmax(0, 1fr);
  gap: var(--drama-space-md);
  padding-top: var(--drama-space-md);
  border-top: 1px solid var(--drama-color-border);
}

.conversation-message__fields dt {
  color: var(--drama-color-text-muted);
  font-family: var(--vgs-font-mono);
  font-size: var(--drama-font-size-xs);
  overflow-wrap: anywhere;
}

.conversation-message__fields dd {
  min-width: 0;
  margin: 0;
}

.conversation-message pre {
  max-width: 100%;
  margin: 0;
  font-family: var(--vgs-font-mono);
  font-size: var(--drama-font-size-xs);
  line-height: 1.6;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.conversation-message__raw {
  margin-top: var(--drama-space-lg);
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.conversation-message__raw summary {
  cursor: pointer;
}

.conversation-message__raw pre {
  max-height: 300px;
  overflow: auto;
  margin-top: var(--drama-space-sm);
  padding: var(--drama-space-md);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-bg);
  color: var(--drama-color-text);
}

@media (max-width: 680px) {
  .conversation-message__bubble {
    width: 100%;
  }

  .conversation-message__fields > div {
    grid-template-columns: 1fr;
  }
}
</style>
